/**
 * MachineScheduleToolbar.tsx
 * Sticky toolbar for the Machine Schedule view (Phase 2, Item 4).
 *
 * Controls:
 *   Today button | Prev/Next arrows |
 *   REQ-04: Start / End date pickers + Apply button (free date window) |
 *   + Add Event button (Phase 2 item 6) |
 *   Show Tentative switch (Phase 2 item 8) — now in URL via useMachineScheduleFilters |
 *   [Phase 2 item 10] Lab Section / Machine / Work Type filter dropdowns
 *
 * REQ-04: the Day/Week/2-Weeks/Month preset toggle was replaced by two free
 * Start / End <TextField type="date"> pickers + an Apply button (Sam's original
 * idea), mirroring MasterGanttToolbar. Prev/Next now shift the window by its
 * own length; Today resets to the default window.
 *
 * FEAT-2026-0007 6E.1 — removed activeQuoteKey, activeWorkOrderKey,
 *                       bookingsInWindow props and the "Edit Tasks" button.
 *                       Entry point for quote/WO planning is Scenario Planning.
 */

import React from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  MenuItem,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today,
  AddCircleOutline,
  Search,
  Clear,
  BarChart,
  DensitySmall,
  DensityMedium,
  DensityLarge,
  TableRows,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { MachineScheduleFilters } from './MachineScheduleFilters';
import type { MachineSummaryForFilter } from './MachineScheduleFilters';
import type { MachineScheduleFilterState } from './useMachineScheduleFilters';
import { SCHEDULE_RANGE_PRESETS, matchRangePreset } from './dateRangePresets';

interface MachineScheduleToolbarProps {
  /** ISO date 'YYYY-MM-DD' — start of the visible (applied) window */
  startDate: string;
  /** ISO date 'YYYY-MM-DD' — end of the visible (applied) window */
  endDate: string;

  // ── REQ-04: free start/end date pickers ────────────────────────────────
  /** Pending Start value in the picker (edited locally, applied on Apply). */
  pendingStart: string;
  /** Pending End value in the picker (edited locally, applied on Apply). */
  pendingEnd: string;
  /** Called as the Start picker changes. */
  onPendingStartChange: (val: string) => void;
  /** Called as the End picker changes. */
  onPendingEndChange: (val: string) => void;
  /** Commit the pending Start/End to the visible window. */
  onApplyWindow: () => void;
  /** Quick-range preset selected → commit that window immediately. */
  onRangePreset: (start: string, end: string) => void;

  /** Navigate to today's window */
  onToday: () => void;
  /** Move window backward by its own length */
  onPrev: () => void;
  /** Move window forward by its own length */
  onNext: () => void;
  /**
   * Phase 2 item 6: callback to open the Add Event dialog.
   * When omitted, the "+ Add Event" button is not rendered.
   */
  onAddEvent?: () => void;

  // ── Phase 2 item 10: filter props ──────────────────────────────────────
  /** Current filter state (from useMachineScheduleFilters). */
  filterState: MachineScheduleFilterState;
  /** All active machines for the Machine dropdown. */
  machines: MachineSummaryForFilter[];
  /** Whether any filter is active. */
  hasActiveFilters: boolean;
  /** Called when filter state changes. */
  onFilterChange: (patch: Partial<MachineScheduleFilterState>) => void;
  /** Called when user clicks "Clear Filters". */
  onClearFilters: () => void;

  // ── M6: capacity heatmap toggle ────────────────────────────────────────
  /** Whether the per-machine capacity heatmap strip is shown. */
  showCapacity?: boolean;
  /** Toggle the capacity heatmap. */
  onToggleCapacity?: () => void;

  // ── M7: density / zoom ──────────────────────────────────────────────────
  /** Current column density. */
  density?: 'compact' | 'normal' | 'wide';
  /** Change the column density (zoom). */
  onDensityChange?: (d: 'compact' | 'normal' | 'wide') => void;

  // ── Sticky-headers UX pass: compact one-line machine rows ───────────────
  /** Whether machine rows render at half height with one-line "code · name" labels. */
  compactRows?: boolean;
  /** Toggle compact machine rows. */
  onToggleCompactRows?: () => void;
}

export const MachineScheduleToolbar: React.FC<MachineScheduleToolbarProps> = ({
  startDate,
  endDate,
  pendingStart,
  pendingEnd,
  onPendingStartChange,
  onPendingEndChange,
  onApplyWindow,
  onRangePreset,
  onToday,
  onPrev,
  onNext,
  onAddEvent,
  filterState,
  machines,
  hasActiveFilters,
  onFilterChange,
  onClearFilters,
  showCapacity,
  onToggleCapacity,
  density,
  onDensityChange,
  compactRows,
  onToggleCompactRows,
}) => {
  const theme = useTheme();

  // REQ-04: enable Apply only when both pickers hold a value and the applied
  // window actually differs from what's pending (avoids a no-op reload).
  const windowDirty = pendingStart !== startDate || pendingEnd !== endDate;
  const canApply = Boolean(pendingStart) && Boolean(pendingEnd) && windowDirty;

  // Quick-range preset dropdown — which preset (if any) the APPLIED window
  // matches; null = a custom window typed into the pickers.
  const activePreset = React.useMemo(
    () => matchRangePreset(startDate, endDate, new Date()),
    [startDate, endDate],
  );
  const handleRangePresetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const preset = SCHEDULE_RANGE_PRESETS.find((p) => p.key === event.target.value);
    if (!preset) return;
    const { startDate: s, endDate: e } = preset.compute(new Date());
    onRangePreset(s, e);
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.border.color}`,
        px: 2,
        py: 1,
      }}
    >
      {/* ── Row 1: navigation + date-window pickers + toggles ─────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          minHeight: 44,
        }}
      >
        {/* Today button */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<Today fontSize="small" />}
          onClick={onToday}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '6px', minWidth: 80 }}
        >
          Today
        </Button>

        {/* Prev / Next navigation — shift the window by its own length */}
        <ButtonGroup size="small" variant="outlined" sx={{ borderRadius: '6px' }}>
          <IconButton size="small" onClick={onPrev} aria-label="Previous period">
            <ChevronLeft fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onNext} aria-label="Next period">
            <ChevronRight fontSize="small" />
          </IconButton>
        </ButtonGroup>

        {/* Quick-range preset dropdown (sets both dates + applies immediately) */}
        <TextField
          select
          label="Quick range"
          size="small"
          value={activePreset ?? 'custom'}
          onChange={handleRangePresetChange}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160, flexShrink: 0 }}
          inputProps={{ 'aria-label': 'Quick date range for Machine Schedule' }}
        >
          {activePreset === null && (
            <MenuItem value="custom" disabled>
              Custom range
            </MenuItem>
          )}
          {SCHEDULE_RANGE_PRESETS.map((p) => (
            <MenuItem key={p.key} value={p.key}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>

        {/* REQ-04: free Start / End date pickers + Apply (replaces preset toggle) */}
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <TextField
            type="date"
            label="Start"
            size="small"
            value={pendingStart}
            onChange={(e) => onPendingStartChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onApplyWindow(); }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Start date for Machine Schedule' }}
            sx={{ width: 150 }}
          />
          <Typography variant="body2" color="text.secondary">
            to
          </Typography>
          <TextField
            type="date"
            label="End"
            size="small"
            value={pendingEnd}
            onChange={(e) => onPendingEndChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onApplyWindow(); }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'End date for Machine Schedule' }}
            sx={{ width: 150 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={onApplyWindow}
            disabled={!canApply}
            sx={{ minWidth: 72, textTransform: 'none', fontWeight: 600, borderRadius: '6px' }}
          >
            Apply
          </Button>
        </Stack>

        {/* Comprehensive search — WO #, quote #, machine, employee, task, notes */}
        <TextField
          size="small"
          placeholder="Search WO #, machine, employee…"
          value={filterState.q}
          onChange={(e) => onFilterChange({ q: e.target.value })}
          sx={{ flex: '0 1 320px', minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: theme.palette.text.disabled }} />
              </InputAdornment>
            ),
            endAdornment: filterState.q ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => onFilterChange({ q: '' })}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />

        <Box sx={{ flex: 1 }} />

        {/* Phase 2 item 6: "+ Add Event" button — opens AddEventDialog */}
        {onAddEvent && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddCircleOutline fontSize="small" />}
            onClick={onAddEvent}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '6px',
              borderColor: theme.schedule.maintenance,
              color: theme.schedule.maintenance,
              '&:hover': {
                borderColor: theme.schedule.maintenance,
                backgroundColor: `${theme.schedule.maintenance}12`,
              },
            }}
          >
            Add Event
          </Button>
        )}

        {/* Phase 2 item 8: "Show Tentative" toggle — now mirrors filterState.showTentative */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={filterState.showTentative}
              onChange={(e) => onFilterChange({ showTentative: e.target.checked })}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: theme.schedule.tentative,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: theme.schedule.tentative,
                },
              }}
            />
          }
          label={
            <Typography variant="caption" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
              Show Tentative
            </Typography>
          }
          sx={{ ml: 0, mr: 0.5 }}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* M6: capacity heatmap toggle */}
        {onToggleCapacity && (
          <Tooltip title="Toggle the per-machine capacity heatmap">
            <ToggleButton
              value="capacity"
              selected={!!showCapacity}
              onChange={() => onToggleCapacity()}
              size="small"
              aria-label="Toggle capacity heatmap"
              sx={{ px: 1, textTransform: 'none', borderRadius: '6px' }}
            >
              <BarChart fontSize="small" sx={{ mr: 0.5 }} />
              Capacity
            </ToggleButton>
          </Tooltip>
        )}

        {/* M7: density / zoom */}
        {onDensityChange && (
          <ToggleButtonGroup
            value={density ?? 'normal'}
            exclusive
            size="small"
            onChange={(_e, val) => { if (val) onDensityChange(val as 'compact' | 'normal' | 'wide'); }}
            aria-label="Column density"
            sx={{ borderRadius: '6px' }}
          >
            <Tooltip title="Compact columns">
              <ToggleButton value="compact" aria-label="Compact"><DensitySmall fontSize="small" /></ToggleButton>
            </Tooltip>
            <Tooltip title="Normal columns">
              <ToggleButton value="normal" aria-label="Normal"><DensityMedium fontSize="small" /></ToggleButton>
            </Tooltip>
            <Tooltip title="Wide columns">
              <ToggleButton value="wide" aria-label="Wide"><DensityLarge fontSize="small" /></ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        )}

        {/* Sticky-headers UX pass: compact one-line machine rows */}
        {onToggleCompactRows && (
          <Tooltip title={compactRows ? 'Comfortable rows (two-line machine labels)' : 'Compact rows (one-line machine labels)'}>
            <ToggleButton
              value="compactRows"
              selected={!!compactRows}
              onChange={() => onToggleCompactRows()}
              size="small"
              aria-label="Toggle compact rows"
              sx={{ px: 1, borderRadius: '6px' }}
            >
              <TableRows fontSize="small" />
            </ToggleButton>
          </Tooltip>
        )}
      </Box>

      {/* ── Row 2: filter dropdowns (Phase 2 item 10) ─────────────── */}
      <Box
        sx={{
          pt: 0.75,
          borderTop: `1px solid ${theme.border.color}`,
          mt: 0.75,
        }}
      >
        <MachineScheduleFilters
          filterState={filterState}
          machines={machines}
          hasActiveFilters={hasActiveFilters}
          onFilterChange={onFilterChange}
          onClearFilters={onClearFilters}
        />
      </Box>
    </Box>
  );
};
